import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Loader from '../../components/Loader';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';
import Swal from 'sweetalert2';
import { Dropdown } from 'primereact/dropdown';
import { formatCurrency, formatDate } from '../../utils/helper';
import { getOrderStatus } from '../../utils/statusHelper';

const Details = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoader, setIsLoader] = useState(false);
    const [order, setOrder] = useState(null);
    const [status, setStatus] = useState('');
  const [amountPaid, setAmountPaid] = useState(0);
    const [paymentStatus, setPaymentStatus] = useState('pending');

    useEffect(() => {
        if (order) {
            const total = parseFloat(order.total_amount || 0);
            const paid = parseFloat(amountPaid || 0);
            if (paid >= total) {
                setPaymentStatus('paid');
            } else if (paid > 0) {
                setPaymentStatus('partially_paid');
            } else {
                setPaymentStatus('pending');
            }
        }
    }, [amountPaid, order?.total_amount]);

    const [readyAt, setReadyAt] = useState('');
    const [orderType, setOrderType] = useState('Local Order');
    const [waybillNumber, setWaybillNumber] = useState('');

    const statusOptions = [
        { label: 'Pending', value: 'pending' },
        { label: 'Ready Not Collected', value: 'ready_not_collected' },
        { label: 'Complete (Collected)', value: 'completed' },
        { label: 'Cancelled', value: 'cancelled' }
    ];

    const paymentStatusOptions = [
        { label: 'Pending', value: 'pending' },
        { label: 'Partially Paid', value: 'partially_paid' },
        { label: 'Paid', value: 'paid' }
    ];

    const orderTypeOptions = [
        { label: 'Local Order', value: 'Local Order' },
        { label: 'Waybill Order', value: 'Waybill Order' },
    ];

    useEffect(() => {
        if (id) {
            getSingleData();
        }
    }, [id]);

    const getSingleData = async () => {
        setIsLoader(true);
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: `admin/orders/${id}`,
            });
            const data = response.data;
            if (data.status) {
                setOrder(data.data);
                setStatus(data.data.status);
                setAmountPaid(data.data.amount_paid || 0);
                setPaymentStatus(data.data.payment_status || 'pending');
                setOrderType(data.data.order_type || 'Local Order');
                setWaybillNumber(data.data.waybill_number || '');
                // Format ready_at for datetime-local input (YYYY-MM-DDTHH:MM)
                let readyAtVal = '';
                if (data.data.ready_at) {
                    // Assuming data.data.ready_at is "YYYY-MM-DD HH:MM:SS"
                    readyAtVal = data.data.ready_at.replace(' ', 'T').substring(0, 16);
                }
                setReadyAt(readyAtVal);
            } else {
                toast.error(data.message);
                navigate('/orders');
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to load order data');
            navigate('/orders');
        } finally {
            setIsLoader(false);
        }
    };

    const handleStatusUpdate = async () => {
        if (!status) return;

        // If cancelling, show confirmation
        if (status === 'cancelled' && order.status !== 'cancelled') {
            const result = await Swal.fire({
                title: 'Cancel Order?',
                text: "Are you sure you want to cancel this order? Stock will be restored.",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, cancel it!'
            });
            if (!result.isConfirmed) return;
        }
        
        setIsLoader(true);
        try {
            const response = await ApiService.request({
                method: 'PUT',
                url: `admin/orders/${id}`,
                data: { 
                    status,
                    amount_paid: amountPaid,
                    payment_status: paymentStatus,
                    ready_at: readyAt,
                    order_type: orderType,
                    waybill_number: waybillNumber,
                },
            });

            if (response.data.status) {
                toast.success(response.data.message);
                getSingleData(); // Refresh data
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Something went wrong');
        } finally {
            setIsLoader(false);
        }
    };

    if (isLoader && !order) return <Loader />;

    if (!order) return null;

    const { badgeClass, label: statusLabel } = getOrderStatus(order.status);

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Order Details #{order.id}
                        </h1>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <Link to="/orders" className="btn btn-sm fw-bold btn-secondary">
                            Back to List
                        </Link>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="row g-5 g-xl-10">
                        {/* Left Column: Order Info & Items */}
                        <div className="col-xl-8">
                            <div className="card mb-5 mb-xl-10">
                                <div className="card-header">
                                    <div className="card-title m-0">
                                        <h3 className="fw-bold m-0">Order Summary</h3>
                                    </div>
                                    <div className="card-toolbar">
                                        <span className={`badge ${badgeClass} fs-7 fw-bold`}>{statusLabel}</span>
                                    </div>
                                </div>
                                <div className="card-body p-9">
                                    <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-5 mb-10">
                                        <div className="col">
                                            <div className="fs-6 fw-bold text-gray-600 mb-2">Date</div>
                                            <div className="fs-5 fw-bold text-gray-800">{formatDate(order.created_at)}</div>
                                        </div>
                                        <div className="col">
                                            <div className="fs-6 fw-bold text-gray-600 mb-2">Total Amount</div>
                                            <div className="fs-5 fw-bold text-gray-800">{formatCurrency(order.total_amount)}</div>
                                        </div>
                                        <div className="col">
                                            <div className="fs-6 fw-bold text-gray-600 mb-2">Amount Paid</div>
                                            <div className="fs-5 fw-bold text-success">{formatCurrency(order.amount_paid || 0)}</div>
                                        </div>
                                        <div className="col">
                                            <div className="fs-6 fw-bold text-gray-600 mb-2">Pending Amount</div>
                                            <div className="fs-5 fw-bold text-danger">{formatCurrency(order.total_amount - (order.amount_paid || 0))}</div>
                                        </div>
                                        <div className="col">
                                            <div className="fs-6 fw-bold text-gray-600 mb-2">Payment Status</div>
                                            <div className="fs-5 fw-bold text-gray-800">
                                                <span className={`badge badge-light-${order.payment_status === 'paid' ? 'success' : order.payment_status === 'partially_paid' ? 'warning' : 'danger'} fw-bold`}>
                                                    {order.payment_status ? order.payment_status.replace('_', ' ').toUpperCase() : 'PENDING'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="col">
                                            <div className="fs-6 fw-bold text-gray-600 mb-2">Order Type</div>
                                            <div className="fs-5 fw-bold text-gray-800">{order.order_type || 'Local Order'}</div>
                                        </div>
                                        {order.order_type === 'Waybill Order' && (
                                            <div className="col">
                                                <div className="fs-6 fw-bold text-gray-600 mb-2">Waybill Number</div>
                                                <div className="fs-5 fw-bold text-gray-800">{order.waybill_number || '-'}</div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                                            <thead>
                                                <tr className="fw-bold text-muted">
                                                    <th className="min-w-150px">Product</th>
                                                    <th className="min-w-100px text-center">Unit</th>
                                                    <th className="min-w-100px text-end">Price</th>
                                                    <th className="min-w-100px text-end">Qty</th>
                                                    <th className="min-w-100px text-end">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {order.items && order.items.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div className="d-flex justify-content-start flex-column">
                                                                    <span className="text-dark fw-bold text-hover-primary mb-1 fs-6">{item.product_name}</span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="text-center">{item.unit || 'piece'}</td>
                                                        <td className="text-end">{formatCurrency(item.price)}</td>
                                                        <td className="text-end">{item.quantity}</td>
                                                        <td className="text-end">{formatCurrency(item.price * item.quantity)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr className="fw-bold fs-6">
                                                    <td colSpan="3" className="text-end">Total:</td>
                                                    <td className="text-end text-dark">{formatCurrency(order.total_amount)}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Column: Customer & Status */}
                        <div className="col-xl-4">
                            {/* Customer Details */}
                            <div className="card mb-5 mb-xl-10">
                                <div className="card-header">
                                    <div className="card-title">
                                        <h3 className="fw-bold m-0">Customer Details</h3>
                                    </div>
                                </div>
                                <div className="card-body p-9">
                                    <div className="mb-5">
                                        <div className="fs-6 fw-bold text-gray-600 mb-2">Name</div>
                                        <div className="fs-5 fw-bold text-gray-800">{order.customer_name}</div>
                                    </div>
                                    <div className="mb-5">
                                        <div className="fs-6 fw-bold text-gray-600 mb-2">Email</div>
                                        <div className="fs-5 fw-bold text-gray-800">{order.customer_email}</div>
                                    </div>
                                    <div className="mb-5">
                                        <div className="fs-6 fw-bold text-gray-600 mb-2">Phone</div>
                                        <div className="fs-5 fw-bold text-gray-800">{order.customer_phone}</div>
                                    </div>
                                    <div className="mb-0">
                                        <div className="fs-6 fw-bold text-gray-600 mb-2">Address</div>
                                        <div className="fs-5 fw-bold text-gray-800">{order.shipping_address || 'N/A'}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Status Update */}
                            <div className="card mb-5 mb-xl-10">
                                <div className="card-header">
                                    <div className="card-title">
                                        <h3 className="fw-bold m-0">Update Status</h3>
                                    </div>
                                </div>
                                <div className="card-body p-9">
                                    <div className="mb-5">
                                        <label className="form-label fw-bold">Order Status</label>
                                        <Dropdown 
                                            value={status} 
                                            options={statusOptions} 
                                            onChange={(e) => setStatus(e.value)} 
                                            className="w-100"
                                        />
                                    </div>
                                    <div className="mb-5">
                                        <label className="form-label fw-bold">Order Type</label>
                                        <Dropdown
                                            value={orderType}
                                            options={orderTypeOptions}
                                            onChange={(e) => setOrderType(e.value)}
                                            className="w-100"
                                        />
                                    </div>
                                    {orderType === 'Waybill Order' && (
                                        <div className="mb-5">
                                            <label className="form-label fw-bold">Waybill Number</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={waybillNumber}
                                                onChange={(e) => setWaybillNumber(e.target.value)}
                                                placeholder="Enter Waybill Number"
                                            />
                                        </div>
                                    )}
                                    <div className="mb-5">
                                        <label className="form-label fw-bold">Order Ready Date</label>
                                        <input 
                                            type="datetime-local" 
                                            className="form-control" 
                                            value={readyAt} 
                                            onChange={(e) => setReadyAt(e.target.value)} 
                                        />
                                    </div>
                                    <div className="mb-5">
                                        <label className="form-label fw-bold">Amount Paid</label>
                                        <input 
                                            type="number" 
                                            className="form-control" 
                                            value={amountPaid} 
                                            onChange={(e) => setAmountPaid(e.target.value)} 
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>
                                    <div className="mb-5">
                                        <label className="form-label fw-bold">Payment Status</label>
                                        <Dropdown 
                                            value={paymentStatus} 
                                            options={paymentStatusOptions} 
                                            onChange={(e) => setPaymentStatus(e.value)} 
                                            className="w-100"
                                        />
                                    </div>
                                    <button 
                                        onClick={handleStatusUpdate} 
                                        className="btn btn-base w-100"
                                        disabled={isLoader}
                                    >
                                        {isLoader ? 'Updating...' : 'Update Status'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Details;
